import { useCallback, useEffect, useState } from "react";
import { useStripe } from "./useStripe";
import { FEEDBACK_MESSAGES } from "./constants";
import { useShowSnackbar } from "hooks/useShowSnackbar";

import {
  savePaymentMethod as savePaymentMethodRequest,
  getPaymentMethods
} from "./requests";
import { INTERNAL_PATHS } from "routes/paths";

const ADDRESS_VALID_CHARS = new RegExp(/^[0-9a-zA-Z /:,-]+$/);
const NAME_VALID_CHARS = new RegExp(/^[a-zA-Z ]+$/);

export const useCheckoutPayment = ({ setDisableSubmitPayment, setPayment }) => {
  const { addNotification } = useShowSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [paymentData, setPaymentData] = useState({});
  const [isDdOpen, setIsDdOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [paymentMethodErrorMessage, setPaymentMethodErrorMessage] =
    useState(null);
  const { initializeStripe, stripeElements } = useStripe({ isFormOpen });

  const getPaymentData = useCallback(
    async (update) => {
      if (!paymentData.success || update) {
        setIsLoading(true);
        try {
          const response = await getPaymentMethods();
          setPaymentData(response);

          if (response.success === true && !response.data.length) {
            setIsFormOpen(true);
            setIsLoading(false);
            setDisableSubmitPayment(true);
            return;
          }
          await setPayment(response.data[0].uuid);
          setIsLoading(false);
        } catch (err) {
          setIsLoading(false);
          if (window.location.pathname !== INTERNAL_PATHS.SECONDARY_CHECKOUT) {
            return;
          }
          addNotification({
            message: FEEDBACK_MESSAGES.PAYMENT_FETCH_ERROR,
            variant: "error",
            closeButton: false
          });
          window.scrollTo(0, 0);
        }
      }
    },
    [paymentData.success, addNotification, setDisableSubmitPayment, setPayment]
  );

  const savePaymentMethod = useCallback(
    async (paymentMethod) => {
      if (paymentMethod) {
        setPaymentMethodErrorMessage(null);

        try {
          const response = await savePaymentMethodRequest({ paymentMethod });

          if (!response.success) {
            const apiErrorMessage = response?.errors?.[0]?.message;
            setPaymentMethodErrorMessage(
              apiErrorMessage ?? FEEDBACK_MESSAGES.SAVE_PAYMENT_DEFAULT_ERROR
            );
          }

          setDisableSubmitPayment(false);
          await getPaymentData(true);
          setIsFormOpen(false);
          setDisableSubmitPayment(false);
        } catch (err) {
          const apiErrorMessage = err.response?.data?.errors?.[0];
          const errorMessage =
            apiErrorMessage?.message || FEEDBACK_MESSAGES.CHECKOUT_ERROR;
          setPaymentMethodErrorMessage(errorMessage);
        } finally {
          setPaymentSaving(false);
          setIsLoading(false);
        }
      }
    },
    [getPaymentData, setDisableSubmitPayment]
  );

  const handlePaymentError = (error) => {
    const message = error?.message || error;
    addNotification({ message, variant: "error", closeButton: false });
    setPaymentSaving(false);
    setIsLoading(false);
    window.scrollTo(0, 0);
  };

  const submitPaymentMethod = async () => {
    try {
      if (stripeElements.addressElement) {
        const addressValues = await stripeElements.addressElement.getValue();

        setPaymentSaving(true);

        const response = await validateForm({
          addressValues
        });

        if (!response) return;

        const { name, line1, line2, city, postal_code, state } = response;

        const cardData = {
          name,
          address_line1: line1,
          address_line2: line2 || "",
          address_city: city,
          address_state: state,
          address_zip: postal_code,
          address_country: "US"
        };

        const stripeResult = await stripeElements.stripe.createToken(
          stripeElements.cardNumber,
          cardData
        );

        if (stripeResult.error) {
          setIsLoading(false);
          handlePaymentError(stripeResult.error.message);
        } else {
          savePaymentMethod(stripeResult.token);
        }
        return stripeResult;
      }
    } catch (error) {
      handlePaymentError(error);
    }
  };

  const validateForm = async ({ addressValues }) => {
    try {
      const isFormIncomplete = !addressValues.complete;

      if (isFormIncomplete)
        throw new Error(FEEDBACK_MESSAGES.BILLING_ADDRESS_INCOMPLETE_ERROR);

      const { value } = addressValues;
      const { name, address } = value;
      const { line1, line2, city } = address;

      const isNameIncomplete = name.split(" ").length < 2;
      const hasInvalidCharacters =
        !NAME_VALID_CHARS.test(name) ||
        !NAME_VALID_CHARS.test(city) ||
        !ADDRESS_VALID_CHARS.test(line1) ||
        (line2 && !ADDRESS_VALID_CHARS.test(line2));

      if (isNameIncomplete)
        return handlePaymentError(FEEDBACK_MESSAGES.INCOMPLETE_NAME_ERROR);

      if (hasInvalidCharacters) {
        return handlePaymentError(FEEDBACK_MESSAGES.INVALID_ADDRESS_ERROR);
      }

      return { name, ...address };
    } catch (error) {
      handlePaymentError(error);
    }
  };

  useEffect(() => {
    getPaymentData();
  }, [getPaymentData]);

  return {
    isLoading,
    isDdOpen,
    isFormOpen,
    paymentData,
    paymentSaving,
    paymentMethodErrorMessage,
    getPaymentData,
    openDropDown: setIsDdOpen,
    toggleForm: setIsFormOpen,
    submitPaymentMethod,
    setPaymentMethodErrorMessage,
    initializeStripe
  };
};
